// ─── Technology Versions (issue #1283) ────────────────────────────────────
//
// Nested under a parent `technologyId` (`/technologies/:technologyId/versions`),
// so this does not fit the flat single-id `createEntityCrudClient` shape.

import type { TechnologyVersion } from '../../graphql/types';
import { apiRequest } from './http';

/**
 * Wire-format status accepted by the REST write routes
 * (`backend/src/entities/TechnologyVersion.ts` `TechnologyVersionStatus`
 * nativeEnum). Unlike `ProjectMilestoneStatusInput`, this has no
 * hyphen/underscore mismatch to normalize — the GraphQL enum names and the
 * REST wire values are identical single words.
 */
export type TechnologyVersionStatusInput = 'draft' | 'published' | 'deprecated';

export interface CreateTechnologyVersionInput {
  versionLabel: string;
  status?: TechnologyVersionStatusInput;
  releaseDate?: string;
  notes?: string;
}

export interface UpdateTechnologyVersionInput {
  versionLabel?: string;
  status?: TechnologyVersionStatusInput;
  releaseDate?: string;
  notes?: string;
}

export async function createTechnologyVersion(
  technologyId: string,
  input: CreateTechnologyVersionInput
): Promise<TechnologyVersion> {
  return apiRequest<TechnologyVersion>(`/technologies/${technologyId}/versions`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateTechnologyVersion(
  technologyId: string,
  versionId: string,
  input: UpdateTechnologyVersionInput
): Promise<TechnologyVersion> {
  return apiRequest<TechnologyVersion>(`/technologies/${technologyId}/versions/${versionId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteTechnologyVersion(
  technologyId: string,
  versionId: string,
  hardDelete: boolean = false
): Promise<void> {
  const queryParam = hardDelete ? '?hard=true' : '';
  return apiRequest<void>(`/technologies/${technologyId}/versions/${versionId}${queryParam}`, {
    method: 'DELETE',
  });
}
